import { AxiosError } from 'axios';
import { GetPriceForQuantity, GetPricingLine } from 'features/commerce/pricing/pricing-result';
import arrayMutators from 'final-form-arrays';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Localizer } from 'services/Localizer';
import { ServerData } from 'services/ServerData';
import { getUserData, UserData } from 'services/UserData';
import { getTabDivElement, getTabLiElement } from 'shared-client/types/commerce/tabs';
import { lazyLoadImages } from 'shared/assets/lazy';
import Breadcrumbs from 'shared/components/Breadcrumbs';
import ProductPricing from 'shared/components/commerce/ProductPricing';
import Spinner from 'shared/components/Spinner';
import RocForm from 'shared/edit/RocForm';
import useRocEventHandler, { dispatchRocEvent } from 'shared/hooks/useRocEventHandler';
import { handleAxiosErrorWithValidation } from 'shared/util/Misc';
import { SimpleProductsListing } from './AlternateProductDetails';
import { getPartialProductData } from './data';
import { AddOnsFormValues, getProductAddOnsComponent } from './ProductAddOns';
import ProductAddToCart from './ProductAddToCart';
import ProductAddToList from './ProductAddToList';
import ProductAssets from './ProductAssets';
import ProductDetailsContext, { useProductDetailsContext } from './ProductDetailsContext';
import ProductRating from './ProductRating';
import ProductSelections from './ProductSelections';
import {
	getInitialProductDetailsState,
	ProductAddOnDto,
	ProductDetailsState,
	ProductPartialDataLoaderOptions,
	ProductType,
	TabViewChunk,
} from './types';
import useProductDetails, { UseProductDetails } from './useProductDetails';
// #region HCL
import ProductLists from './ProductLists';
import { GetSalePercentageSummary } from './data.HCL';
import { ProductPriceBreaks } from './ProductPriceBreaks';
// #endregion

const loaderOptions: ProductPartialDataLoaderOptions = {
	initialState: getInitialProductDetailsState,
	dataLoaderFn: getPartialProductData,
};

/**
 * Keys that will be requested from the server when the active product is changed (eg, the user changed selections)
 */
const missingKeys: Array<keyof ProductDetailsState> = [
	'summary',
	'assets',
	'availability',
	'brand',
	'pricingLines',
	'displayPricing',
	'breadcrumbPath',
	'reviewsSummary',
	'tabs',
	'addOns',
];

/**
 * Main component that renders the product details using react portals
 */
function ProductDetails() {
	const UseProductDetailsResult: UseProductDetails = useProductDetails({ loaderOptions });
	const [canGoBack, setCanGoBack] = useState('');
	const {
		state: { productDetails, groupId },
		actor: { setLoggedInUserFirstName, setUserIsSignedIn },
	} = UseProductDetailsResult;

	const getLoggedInUserFirstName = async (): Promise<string> => {
		try {
			const data = (await getUserData()).data;
			getUserIsSignedIn(data);
			return (data && data.summary && data.summary.firstName) || '';
		} catch (error) {
			console.error('Unable to load user data', error);
		}
		return Promise.resolve('');
	};

	const getUserIsSignedIn = (data: UserData) => {
		setUserIsSignedIn((data && data.summary && data.summary.isSignedIn) || false);
	};

	useRocEventHandler(
		'hawk-recs-widget-loaded',
		({ widgetGuid, hasItems }) => {
			// update tab visibility
			if (productDetails.tabs === undefined) {
				return;
			}

			// select based on data attributes to prevent this from breaking due to customizations
			const elements = document.querySelectorAll(`[data-tab-id] [data-widgetguid="${widgetGuid}"]`);

			if (elements.length === 0) {
				console.warn(`No matches found for Widget Guid: ${widgetGuid}`);
				return;
			}

			elements.forEach((el) => {
				// have to check for this again inside this closure
				if (productDetails.tabs === undefined) {
					return;
				}

				// closest attempts to find the tab parent node.
				const tabDiv = el.closest(`[data-tab-id]`);
				if (!tabDiv) {
					console.warn('Unable to find tab parent!');
					return;
				}

				// grab the tab-id rendered by the server and find the matching tabChunk
				const tabId = tabDiv.getAttribute('data-tab-id');
				const tabChunk = productDetails.tabs.find((t) => t.tab.id === tabId);

				if (!tabChunk) {
					console.warn(`Unable to find tab (${tabId})`);
					return;
				}

				// let the magic happen
				updateTabVisibility(tabChunk, hasItems);
			});
		},
		[],
	);

	useEffect(() => {
		getLoggedInUserFirstName().then((firstName) => {
			setLoggedInUserFirstName(firstName);
		});

		setCanGoBack(sessionStorage.getItem('canGoBack') || '');
		sessionStorage.removeItem('canGoBack');

		dispatchRocEvent('product-viewed', {
			productName: productDetails.summary.name,
			sku: productDetails.summary.sku,
			productId: productDetails.id,
			productGroupId: groupId !== '' ? groupId : null,
			selections: productDetails.selections ?? null,
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<ProductDetailsContext.Provider
			value={{
				state: { ...UseProductDetailsResult.state },
				actor: { ...UseProductDetailsResult.actor },
			}}
		>
			<ProductBreadcrumbs canGoBack={canGoBack} />
			<ProductMedia />
			<ProductHeader />
			{productDetails.summary && productDetails.summary.useAlternateProductDetails ? (
				<SimpleProductsListing />
			) : (
				<ProductSelectionsRegion />
			)}
		</ProductDetailsContext.Provider>
	);
}

//#region React Portals

/**
 * Product page header containing the breadcrumbs
 * @param props
 */
export function ProductBreadcrumbs(props: { canGoBack: string }) {
	const {
		state: { productDetails },
	} = useProductDetailsContext();

	const headerDom = document.getElementById('product-details-header');
	if (!headerDom) {
		console.warn('Header DOM element not found.');
		return null;
	}

	const breadcrumbComponent = (
		<Breadcrumbs
			breadcrumbPath={productDetails.breadcrumbPath}
			backToPage={{ label: Localizer('BackToResults'), url: props.canGoBack }}
		/>
	);

	return ReactDOM.createPortal(breadcrumbComponent, headerDom);
}

/**
 * Product page media component, containing the product images
 */
export function ProductMedia() {
	const {
		state: { productDetails },
		actor: { isLoadingAny },
	} = useProductDetailsContext();

	const mainLeftDom = document.getElementById('product-details-product-media');
	if (!mainLeftDom) {
		console.warn('Product media DOM element not found.');
		return null;
	}

	const productAssets = (
		<>
			{!isLoadingAny(['assets']) ? (
				// #region HCL
				<ProductAssets
					assets={productDetails.assets}
					saleDiscount={GetSalePercentageSummary(productDetails)}
					isNewProduct={productDetails.isNewProduct}
				/>
			) : (
				// #endregion
				<Spinner block />
			)}
		</>
	);

	return ReactDOM.createPortal(productAssets, mainLeftDom);
}

/**
 * Product page main info header, containing the pricing and rating components
 */
export function ProductHeader() {
	// #region HCL -- added productQuantity, userIsSignedIn for guest quote
	const {
		state: { productDetails, hasError, productQuantity, userIsSignedIn },
		actor: { isLoadingAny },
	} = useProductDetailsContext();
	// #endregion

	const mainInfoHeaderDom = document.getElementById('product-details-main-info-header');
	if (!mainInfoHeaderDom) {
		console.warn('Main info header DOM element not found.');
		return null;
	}

	const getDetail = () => {
		if (productDetails.displayPricing == null) {
			throw new Error("Display pricing was rendered when it wasn't ready!");
		}
		const selectedPricingLineId =
			productDetails.pricingLines &&
			productDetails.pricingLines.selected &&
			productDetails.pricingLines.selected.id;

		const displayPriceLine = GetPricingLine(
			productDetails.displayPricing,
			productDetails.id,
			selectedPricingLineId,
		);

		// #region HCL
		return displayPriceLine && GetPriceForQuantity(displayPriceLine.prices, productQuantity ?? 1);
		// #endregion HCL
	};

	// #region HCL
	const hidePriceForGuestQuote = productDetails.summary?.guestQuoteEnabled === true && !userIsSignedIn;
	// #endregion HCL

	const productHeaderInfo = (
		<>
			{!isLoadingAny(['displayPricing']) ? (
				<>
					{/* #region HCL - hide price for guests when Guest Quote Enabled */}
					{!hidePriceForGuestQuote && <ProductPriceBreaks />}
					{!hidePriceForGuestQuote && (
						<ProductPricing
							displayPrice={getDetail()}
							className="roc-pdp-price"
							inline
							testId="pdp-main-price"
						/>
					)}
					{/* #endregion */}
				</>
			) : !hasError ? (
				<Spinner block />
			) : null}

			{!isLoadingAny(['reviewsSummary']) ? <ProductRating /> : <Spinner block />}
		</>
	);

	return ReactDOM.createPortal(productHeaderInfo, mainInfoHeaderDom);
}

/**
 * Product page product selection component, which takes care of the logic and display
 * of the product selections and pricing lines / units of measure
 */
export function ProductSelectionsRegion() {
	const [selectedProductPricingLineId, setSelectedProductPricingLineId] = useState<string | null>(null);
	const {
		state: { productDetails, groupId, hasError, productStateOverride, userIsSignedIn, productQuantity },
		actor: { isLoadingAny, setSimpleProductLoading, setProductStateOverride, setHasError, setProductQuantity },
	} = useProductDetailsContext();

	const [productAddOns, setProductAddOns] = useState<ProductAddOnDto[]>();
	const [childProductAddOnNames, setChildProductAddOnNames] = useState<string[]>();

	useEffect(() => {
		if (productDetails.addOns) {
			setProductAddOns([...(productDetails.addOns.productAddOnData ?? [])]);
			setChildProductAddOnNames([...(productDetails.addOns.availableChildProductAddOns ?? [])]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productDetails.addOns]);

	const productSelectionsDom = document.getElementById('product-details-selections');
	if (!productSelectionsDom) {
		console.warn('Product selections DOM element not found.');
		return null;
	}

	const ProductAddOnsComponent = getProductAddOnsComponent();

	const productSelections = (
		<div>
			{!hasError ? (
				<>
					{!isLoadingAny(['selections']) && productDetails.selections && productDetails.children ? (
						<ProductSelections
							selections={productDetails.selections}
							simpleProducts={productDetails.children}
							onSimpleProductSelected={async (simpleProduct) => {
								console.debug('Simple Product Selected:', simpleProduct.name);

								setSimpleProductLoading(true);

								try {
									const simpleProductData = await getPartialProductData({
										id: simpleProduct.id,
										missingKeys: missingKeys,
									});

									console.debug('Simple Product Data:', simpleProductData);

									const newProductData: ProductDetailsState = {
										...productStateOverride,
										...productDetails,
										...simpleProductData,
									};

									setProductStateOverride({
										id: simpleProduct.id,
										...simpleProductData,
									});

									// Function to change the url, title, product name, brand information, sku and manufacturer part number
									UpdateSSRData(newProductData);

									dispatchRocEvent('product-selection-updated', {
										product: {
											productId: simpleProduct.id,
											productDetails: newProductData,
										},
									});
								} catch (error) {
									console.error(error);
									setHasError(error);
								} finally {
									setSimpleProductLoading(false);
								}
							}}
							onSimpleProductCleared={async () => {
								setSimpleProductLoading(true);
								try {
									const groupProductData = await getPartialProductData({
										id: groupId,
										missingKeys: missingKeys,
									});

									setProductStateOverride({
										id: groupId,
										...groupProductData,
									});

									// Function to change the url, title, product name, brand information, and sku
									UpdateSSRData(groupProductData);

									const newGroupProductData = {
										...productDetails,
										...groupProductData,
									};

									dispatchRocEvent('product-selection-updated', {
										product: {
											productId: groupId,
											productDetails: newGroupProductData,
										},
									});
								} catch (error) {
									console.error(error);
									setHasError(error);
								} finally {
									setSimpleProductLoading(false);
								}
							}}
						/>
					) : null}

					<RocForm<AddOnsFormValues>
						initialValues={{ addOns: productAddOns }}
						onSubmit={(values) => {
							if (values.axiosError) {
								return handleAxiosErrorWithValidation(values.axiosError);
							}
							return;
						}}
						mutators={{
							...arrayMutators,
						}}
						subscription={{
							submitting: true,
						}}
						validateOnBlur={true}
						render={({ form, handleSubmit }) => {
							const addOnsHandleSubmitWithError = (error: AxiosError) => {
								// `form.change` is synchronous, and we don't have access to anything except field values in the `onSubmit` function for the form,
								// so we store the error in the `axiosError` field, so we have access to it inside the `onSubmit` function and can handle it there
								form.change('axiosError', error);
								handleSubmit();
							};
							return (
								<>
									{!isLoadingAny(['addOns']) && (
										<ProductAddOnsComponent
											addOns={productAddOns ?? []}
											childAddOnNames={childProductAddOnNames ?? []}
											formApi={form}
											setProductAddOns={setProductAddOns}
											hideAddOnsDetails={productDetails?.summary?.type == ProductType.Group}
										/>
									)}

									{!isLoadingAny(['pricingLines']) && (
										<div>
											<ProductAddToCart
												id={productDetails.id}
												summary={productDetails.summary}
												availability={productDetails.availability}
												pricingLines={productDetails.pricingLines}
												quantity={productQuantity}
												setQuantity={setProductQuantity}
												setSelectedProductPricingLineId={setSelectedProductPricingLineId}
												onPricingLineChange={async (selectedPricingLineId: string) => {
													try {
														// reload availability for the selected Unit of Measure
														const availability = await getPartialProductData({
															id: productDetails.id,
															missingKeys: ['availability'],
															additionalParams: {
																unitOfMeasureId: selectedPricingLineId,
															},
														});

														const selectedPricingLine =
															productDetails.pricingLines &&
															productDetails.pricingLines.available.find(
																(line) => line.id === selectedPricingLineId,
															);

														if (!selectedPricingLine) {
															throw new Error('Invalid unit of measure selected');
														}

														// override state with and availability from the server
														// and selected unit of measure
														setProductStateOverride({
															...productStateOverride,
															...availability,
															selections: productDetails.selections,
															children: productDetails.children,
															pricingLines: {
																selected: selectedPricingLine,
																available: productDetails.pricingLines
																	? productDetails.pricingLines.available
																	: [],
															},
														});
													} catch (error) {
														console.error(error);
														setHasError(error);
													}
												}}
												addOns={productAddOns}
												addOnsHandleSubmitWithError={addOnsHandleSubmitWithError}
											/>

											<ProductAddToList
												id={productDetails.id}
												summary={productDetails.summary}
												isUserSignedIn={userIsSignedIn}
												quantity={productQuantity}
												selectedProductPricingLineId={selectedProductPricingLineId}
												addOns={productAddOns}
												addOnsHandleSubmitWithError={addOnsHandleSubmitWithError}
											/>
											{/* #region HCL */}
											{userIsSignedIn && <ProductLists productId={productDetails.id} />}
											{/* //#endregion */}
										</div>
									)}
								</>
							);
						}}
					/>
				</>
			) : (
				<div>{Localizer('ErrorOccurredLoadingProductData')}</div>
			)}
		</div>
	);

	return ReactDOM.createPortal(productSelections, productSelectionsDom);
}

//#endregion

function updateTabContents(tabChunk: TabViewChunk<unknown>) {
	console.debug('Updating tab contents:', tabChunk.tab.id);

	// Update SSR rendered HTML
	if (tabChunk.rawHtml == null) {
		return;
	}

	const div = getTabDivElement(tabChunk.tab.id);
	const li = getTabLiElement(tabChunk.tab.id);
	let hidden = false;

	if (div != null) {
		div.outerHTML = tabChunk.rawHtml;
		const mutatedDiv = getTabDivElement(tabChunk.tab.id);
		if (mutatedDiv?.classList.contains('roc-hidden')) {
			hidden = true;
		}
	}

	if (li != null) {
		li.className = hidden ? 'roc-hidden' : 'roc-pdp-sections__nav-item'; // This should match the values set in \Roc.Web\Features\Commerce\Products\Index.cshtml
	}
}

function updateTabVisibility(tabChunk: TabViewChunk<unknown>, visible: boolean) {
	console.debug('Updating tab visbility:', tabChunk.tab.id);

	const div = getTabDivElement(tabChunk.tab.id);
	const li = getTabLiElement(tabChunk.tab.id);

	if (div != null) {
		div.classList.toggle('roc-hidden', !visible);
	}

	if (li != null) {
		li.classList.toggle('roc-hidden', !visible);
		li.classList.toggle('roc-pdp-sections__nav-item', visible);
	}
}

/**
 * Function that updates the data on the page that has been server-side rendered when a simple product is chosen
 * @param productData
 */
function UpdateSSRData(productData: Partial<ProductDetailsState>) {
	const { summary, brand } = productData;

	// update URL and page title
	if (summary?.meta?.pageTitle && ServerData.PageTitleSuffix) {
		// update the url and page title
		window.history.replaceState('', summary.meta.pageTitle + ServerData.PageTitleSuffix, summary.productUrl);

		// replace state title is not working in chrome
		document.title = summary.meta.pageTitle + ServerData.PageTitleSuffix;
	}

	// Handle updating the product branding
	const brandDiv = getElementByTestId('product-page-brand-div');
	const brandName = getElementByTestId('product-page-brand-name');
	const brandLogo = getElementByTestId('product-page-brand-logo') as HTMLImageElement;

	if (!brandDiv || !brandName || !brandLogo) {
		console.warn(
			'Could not find one of the following DOM elements to change:',
			'Brand enclosing div, Brand name, Brand logo',
		);
	} else {
		if (brand && brand.name) {
			brandDiv.classList.remove('roc-hidden');
			brandName.textContent = brand.name;
			if (brand.logoUrl) {
				brandLogo.src = brand.logoUrl;
			}
		} else {
			brandDiv.classList.add('roc-hidden');
			brandName.textContent = '';
			brandLogo.src = '';
		}
	}

	// update tabs with new ssr'd contents
	const tabs = productData.tabs ?? [];
	if (tabs.length > 0) {
		for (const tabChunk of tabs) {
			updateTabContents(tabChunk);
		}
	}

	// Handle updating the product name, SKU text and Manufacturer Part Number text
	const productName = getElementsByTestId('product-page-product-name');

	const productSkuContainer = getElementByTestId('product-page-sku-container');
	const productSkuText = getElementByTestId('product-page-sku-text');
	const productSkuValue = getElementByTestId('product-page-sku-value');

	const productMpnContainer = getElementByTestId('product-page-mpn-container');
	const productMpnText = getElementByTestId('product-page-mpn-text');
	const productMpnValue = getElementByTestId('product-page-mpn-value');

	if (!productName) {
		console.warn('Could not find the Product Name DOM element to change');
	} else if (summary != null) {
		productName.forEach((element) => {
			element.textContent = summary.name;
		});
	}

	if (!productSkuContainer || !productSkuText || !productSkuValue) {
		console.warn('Could not find the Product SKU Container, Text, or Value DOM elements to change');
	} else {
		if (summary?.sku && summary.type != 'Group') {
			productSkuContainer.classList.remove('roc-hidden');
			// #region HCL - replace SKU for ItemNumber
			productSkuText.textContent = `${Localizer('ItemNumber')}:`;
			// #endregion
			productSkuValue.textContent = summary.sku;
		} else {
			productSkuContainer.classList.add('roc-hidden');
			productSkuText.textContent = '';
			productSkuValue.textContent = '';
		}
	}

	if (!productMpnContainer || !productMpnText || !productMpnValue) {
		console.warn(
			'Could not find the Product Manufacturer Part Number Container, Text, or Value DOM elements to change',
		);
	} else {
		if (summary?.manufacturerPartNumber) {
			productMpnContainer.classList.remove('roc-hidden');
			productMpnText.textContent = `${Localizer('ManufacturerPartNumber')}:`;
			productMpnValue.textContent = summary.manufacturerPartNumber;
		} else {
			productMpnContainer.classList.add('roc-hidden');
			productMpnText.textContent = '';
			productMpnValue.textContent = '';
		}
	}

	// #region HCL
	const productGroupName = getElementsByTestId('product-page-parent-product-name');

	if (!productName) {
		console.warn('Could not find the Product Name DOM element to change');
	} else if (summary != null) {
		productName.forEach((element) => {
			element.textContent = summary.name;
		});
	}

	if (!productGroupName) {
		console.warn('Could not find the Parent Product Name Container');
	} else if (summary != null) {
		productGroupName.forEach((element) => {
			if (summary?.productGroupName) {
				element.classList.remove('roc-hidden');
				element.textContent = summary.productGroupName;
			} else {
				element.classList.add('roc-hidden');
				element.textContent = '';
			}
		});
	}
	// #endregion

	lazyLoadImages();
}

/**
 * Simple helper function to get a DOM element by its data-testid attribute
 * @param testId
 */
function getElementByTestId(testId: string): Element | null {
	return document.querySelector(`[data-testid="${testId}"]`);
}

/**
 * Simple helper function to get DOM elements by its data-testid attribute
 * @param testId
 */
function getElementsByTestId(testId: string): NodeListOf<Element> | null {
	return document.querySelectorAll(`[data-testid="${testId}"]`);
}

export default ProductDetails;
